<template>
  <div class="topWaper">
    <div class="month">{{ dateToStringMonthYear(top.period) }}</div>
    <div
      v-for="(waper, index) in top.wapers"
      :key="waper.user?.slug"
      class="waper-container"
    >
      <NuxtLink
        :to="`/wapers/${waper.user?.slug}`"
        class="waper-item"
      >
        <div class="img-container">
          <NuxtImg
            v-if="waper.user?.avatar"
            :src="waper.user.avatar.url"
            :alt="`photo de profil de ${waper.user.nickname}`"
            width="168"
            height="168"
            loading="lazy"
            quality="80"
            fit="cover"
            format="auto"
          />
        </div>
        <div class="text-container">
          <p class="name">
            {{ readMore(capitalize(waper.user?.nickname), 25) }}
          </p>
          <p class="points">{{ numberToString(waper.points) }} points</p>
        </div>
      </NuxtLink>
      <div
        class="position"
        :class="`position-${index + 1}`"
      >
        {{ index + 1 }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TopWapers } from '~/types'

type TopWaterProps = {
  top: TopWapers
}

defineProps<TopWaterProps>()
</script>

<style lang="scss">
.topWaper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: 15.2rem;
  color: $white;
  background: $secondaryBlue;

  .waper-item {
    display: flex;
    align-items: center;
    width: 35rem;
    height: 8.4rem;
    color: $white;
    text-decoration: none;

    &.flexible {
      width: 100%;
    }

    &.dark {
      .text-container {
        color: $dark;
      }
    }

    .img-container {
      width: 8.4rem;
      height: 8.4rem;
      margin-right: 3rem;
      overflow: hidden;
      background: rgb(0 0 0 / 0.2);
      border-radius: 4.2rem;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .text-container {
      text-align: left;

      .name {
        margin-bottom: 0.2rem;
        font-size: 1.6rem;
        font-weight: bold;
        line-height: 2.1rem;
      }

      .points {
        font-size: 1.4rem;
        font-weight: 300;
        line-height: 1.8rem;
        opacity: 0.5;
      }
    }
  }

  .month {
    width: 38.2rem;
    padding-left: 5rem;
    font-size: 1.2rem;
    font-weight: 500;
    letter-spacing: 0.15rem;
    text-transform: uppercase;
  }

  .waper-container {
    position: relative;
    display: flex;
    align-items: center;
    width: 31.3rem;
    height: 100%;
    padding: 3rem;
    border-left: 1px solid $blue;

    .waper-item {
      .img-container {
        width: 10rem;
        height: 10rem;
        border-radius: 5rem;
      }

      .text-container {
        .points {
          font-size: 1.2rem;
          opacity: 1;
        }
      }
    }

    .position {
      position: absolute;
      top: 2rem;
      left: 10rem;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 4.1rem;
      height: 4.1rem;
      overflow: hidden;
      color: $white;
      font-size: 1.5rem;
      font-weight: bold;
      border-radius: 2.1rem;

      &.position-1 {
        background: #fdd013;
      }

      &.position-2 {
        background: #aaa;
      }

      &.position-3 {
        background: #d58300;
      }
    }
  }
}

@media screen and (max-width: 1600px) {
  .topWaper {
    height: 11.5rem;
    padding-right: 3rem;

    .month {
      flex-grow: 1;
      width: 21rem;
      padding-right: 3rem;
      padding-left: 3rem;
    }

    .waper-container {
      max-width: 25rem;
      padding: 0 2.3rem;

      .waper-item {
        .img-container {
          width: 7.7rem;
          height: 7.7rem;
          border-radius: 3.9rem;
        }

        .text-container {
          .name {
            font-size: 1.2rem;
            line-height: 1.6rem;
          }

          .points {
            font-size: 1.1rem;
            line-height: 1.5rem;
          }
        }
      }

      .position {
        top: 1.5rem;
        left: 7.8rem;
        width: 3.1rem;
        height: 3.1rem;
        font-size: 1rem;
        border-radius: 1.6rem;
      }
    }
  }
}

@media screen and (max-width: 1023px) {
  .topWaper {
    flex-direction: column;
    height: auto;
    padding-bottom: 0.7rem;

    .month {
      width: 100%;
      height: 4.8rem;
      font-size: 0.8rem;
      line-height: 4.8rem;
      text-align: center;
    }

    .waper-container {
      width: 100%;
      max-width: 100%;
      height: 11.3rem;
      padding: 0 3rem 0 4.5rem;
      border-top: 1px solid $blue;
      border-left: none;

      .waper-item {
        width: 100%;
        padding: 0;

        .img-container {
          width: 8.3rem;
          height: 8.3rem;
          border-radius: 4.2rem;
        }

        .text-container {
          display: flex;
          flex-direction: row;
          flex-grow: 1;
          justify-content: space-between;
          align-items: center;

          .name {
            font-size: 1.4rem;
          }

          .points {
            font-size: 1.4rem;
          }
        }
      }

      .position {
        top: 0.8rem;
        left: 10.3rem;
        width: 3.4rem;
        height: 3.4rem;
        font-size: 1.1rem;
        border-radius: 1.7rem;
      }
    }
  }
}

@media screen and (max-width: 475px) {
  .topWaper {
    flex-direction: column;
    height: auto;
    padding-bottom: 0.7rem;

    .month {
      width: 100%;
      height: 4.8rem;
      font-size: 0.8rem;
      line-height: 4.8rem;
      text-align: center;
    }

    .waper-container {
      height: 11.3rem;
      padding: 0 2.8rem;

      .waper-item {
        .text-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          text-align: left;
        }
      }

      .position {
        top: 0.8rem;
        left: 9.5rem;
        width: 3.4rem;
        height: 3.4rem;
        font-size: 1.1rem;
        border-radius: 1.7rem;
      }
    }
  }
}

@media screen and (max-width: 374px) {
  .topWaper {
    .waper-container {
      height: 9rem;

      .waper-item {
        .text-container {
          .name {
            font-size: 1.3rem;
            line-height: 1.7rem;
          }

          .points {
            font-size: 1.2rem;
            line-height: 1.6rem;
          }
        }

        .img-container {
          width: 7.2rem;
          height: 7.2rem;
          border-radius: 3.6rem;
        }
      }

      .position {
        top: 0.3rem;
        left: 8rem;
        width: 3rem;
        height: 3rem;
        font-size: 1.1rem;
        border-radius: 1.5rem;
      }
    }
  }
}
</style>
